import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Eye,
  Copy,
  Check,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantName } from "@/shared/utils";

const statusConfigDefault = {
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Processing" },
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Success" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
  expired: { icon: Clock, color: "bg-slate-100 text-slate-700", label: "Expired" },
  cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Cancelled" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Declined" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: t("processing") },
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("success") },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("failed") },
    expired: { icon: Clock, color: "bg-slate-100 text-slate-700", label: t("expired") },
    cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("cancelled") },
    declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("declined") || "Declined" },
  };
};

export default function AdminTransactionTable({ 
  transactions, 
  isLoading, 
  onView,
  showMerchant = true,
  showReseller = true,
  showEngine = false,
  compact = false,
  merchants = [],
  resellers = [],
  engines = [],
  isQRCode = false,
}) {
  // Helper to get merchant name - prefer stored name, fallback to lookup
  const getMerchantNameForTx = (tx) => {
    if (isQRCode && tx.merchant) {
      return getMerchantName(tx.merchant, language, t);
    }
    if (tx.merchant_name) {
      // Try to translate the stored merchant name
      return getMerchantName({ name: tx.merchant_name }, language, t);
    }
    const merchant = merchants.find(m => m.id === tx.merchant_id || m.code === tx.merchant_code);
    if (merchant) {
      return getMerchantName(merchant, language, t);
    }
    return tx.merchant_code || "—";
  };

  // Helper to get reseller name - prefer stored name, fallback to lookup
  const getResellerName = (tx) => {
    if (isQRCode) {
      // For QR codes, check merchant.createdByResellerId
      if (tx.merchant?.createdByResellerId && tx.reseller) {
        return tx.reseller.name;
      }
      return null;
    }
    if (tx.reseller_name) return tx.reseller_name;
    const reseller = resellers.find(r => r.id === tx.reseller_id || r.code === tx.reseller_code);
    return reseller?.name || null;
  };

  const getEngineName = (engineId) => {
    const engine = engines.find(e => e.id === engineId || e.code === engineId);
    return engine?.name || engineId || "—";
  };

  // Helper to translate transaction type
  const getTransactionTypeLabel = (type) => {
    if (!t) return type;
    const typeUpper = (type || '').toUpperCase();
    if (typeUpper === "DEPOSIT") {
      return t("deposit");
    } else if (typeUpper === "WITHDRAWAL") {
      return t("withdrawal");
    }
    return type;
  };

  const [copiedId, setCopiedId] = React.useState(null);
  
  let t = null;
  let language = "en";
  try {
    const langContext = useLanguage();
    t = langContext?.t;
    language = langContext?.language || "en";
  } catch (e) {
    // Not wrapped in LanguageProvider
  }
  const statusConfig = getStatusConfig(t);

  const copyId = (id, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">{t ? t("loading") : "Loading transactions..."}</span>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
        <p className="text-slate-500">{t("noTransactionsFound")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="font-semibold">{t ? t("transaction") : "Transaction"}</TableHead>
            {showEngine && <TableHead className="font-semibold">{t ? t("engine") : "Engine"}</TableHead>}
            {showReseller && <TableHead className="font-semibold">{t ? t("reseller") : "Reseller"}</TableHead>}
            {showMerchant && <TableHead className="font-semibold">{t ? t("merchant") : "Merchant"}</TableHead>}
            <TableHead className="font-semibold">{t ? t("provider") : "Provider"}</TableHead>
            <TableHead className="font-semibold">{t ? t("amount") : "Amount"}</TableHead>
            <TableHead className="font-semibold">{t ? t("status") : "Status"}</TableHead>
            <TableHead className="font-semibold">{t ? t("date") : "Date"}</TableHead>
            <TableHead className="font-semibold w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((tx, index) => {
            const statusKey = (tx.status || '').toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.pending;
            const StatusIcon = status.icon;

            return (
              <motion.tr
                key={tx.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.02 * index }}
                className="hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => onView(tx)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
                      <ArrowDownToLine className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <p className="font-mono text-xs text-slate-600">
                          {isQRCode ? (tx.payment_id?.slice(0, 12) || tx.payment_id) : (tx.transaction_id?.slice(0, 12) || tx.transaction_id)}
                        </p>
                        <button
                          onClick={(e) => copyId(isQRCode ? tx.payment_id : tx.transaction_id, e)}
                          className="p-0.5 hover:bg-slate-100 rounded"
                        >
                          {copiedId === (isQRCode ? tx.payment_id : tx.transaction_id) ? (
                            <Check className="h-3 w-3 text-emerald-500" />
                          ) : (
                            <Copy className="h-3 w-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                      {isQRCode && tx.reference && (
                        <Badge variant="outline" className="text-xs">{tx.reference}</Badge>
                      )}
                      {!isQRCode && (
                        <Badge variant="outline" className="text-xs capitalize">{getTransactionTypeLabel(tx.type)}</Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                {showEngine && (
                  <TableCell>
                    <span className="text-sm font-medium">{engines.length > 0 ? getEngineName(tx.engine_id || tx.engine_code) : (tx.engine_code || "—")}</span>
                  </TableCell>
                )}
                {showReseller && (
                  <TableCell>
                    {(() => {
                      const resellerName = getResellerName(tx);
                      return resellerName ? (
                        <span className="text-sm font-medium">{resellerName}</span>
                      ) : (
                        <span className="text-xs text-slate-400">{t ? t("direct") : "Direct"}</span>
                      );
                    })()}
                  </TableCell>
                )}
                {showMerchant && (
                  <TableCell>
                    <span className="text-sm font-medium">{getMerchantNameForTx(tx)}</span>
                  </TableCell>
                )}
                <TableCell>
                  <span className="text-sm">
                    {(() => {
                      const raw = isQRCode ? (tx.client_bank_name || (t ? t("nA") : "N/A")) : (tx.provider_code || (t ? t("nA") : "N/A"));
                      if (raw === "QR Code" && t) return t("qrCode");
                      return raw;
                    })()}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-semibold text-blue-600">
                    ฿{isQRCode ? (tx.amount?.toLocaleString()) : (tx.amount?.toLocaleString())}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge className={`${status.color} gap-1 border-0`}>
                    <StatusIcon className={`h-3 w-3 ${tx.status === "processing" ? "animate-spin" : ""}`} />
                    {status.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-700">
                    {isQRCode 
                      ? (tx.createdAt ? format(new Date(tx.createdAt), compact ? "MM/dd HH:mm" : "MMM d, HH:mm") : "N/A")
                      : (tx.created_date ? format(new Date(tx.created_date), compact ? "MM/dd HH:mm" : "MMM d, HH:mm") : "N/A")
                    }
                  </span>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </motion.tr>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}